import { consentSignals } from "./consent.js";

/**
 * Google Tag Manager, replacing the direct react-ga4 integration. GA4 now
 * lives inside the container; this module only owns the three things the
 * app has to do itself:
 *
 *   1. load the container (once, and never during the prerender)
 *   2. tell it what the visitor consented to
 *   3. push the events the container has no way to observe on its own
 *
 * The Consent Mode *defaults* are NOT here — they have to run before the
 * container loads, and an ES module is always deferred, so they sit as an
 * inline script in index.html. See the comment there.
 */

const GTM_ID = import.meta.env.VITE_GTM_ID;

let injected = false;

function dataLayer() {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

/**
 * The canonical gtag shim: it pushes the `arguments` object itself, not an
 * array of its contents. GTM distinguishes the two, and a plain array is
 * silently ignored for consent commands — which is why this is a function
 * declaration and not an arrow.
 */
function gtag() {
  // eslint-disable-next-line prefer-rest-params
  dataLayer().push(arguments);
}

/**
 * Injects the container. Idempotent, and a no-op in two cases that both
 * matter:
 *
 * - No VITE_GTM_ID (local dev, or a build before the secret was set): the
 *   whole integration goes quiet rather than requesting a broken URL. This
 *   is also why the ID lives here and not in index.html — Vite would
 *   substitute `%VITE_GTM_ID%` there, but leave the placeholder verbatim
 *   when the variable is missing, and the page would then request
 *   `gtm.js?id=%VITE_GTM_ID%` on every load.
 * - `window.__TL_PRERENDER__`, set by scripts/prerender.js before any page
 *   script runs: page.content() serializes the live DOM, so a container
 *   script tag injected here would be baked into all 84 snapshots — with
 *   the build machine's container ID, on top of the runtime injection.
 */
export function initTagManager() {
  if (injected || !GTM_ID) return;
  if (typeof window === "undefined" || window.__TL_PRERENDER__) return;
  injected = true;
  dataLayer().push({ "gtm.start": Date.now(), event: "gtm.js" });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(GTM_ID)}`;
  document.head.appendChild(script);
}

/**
 * Hands the visitor's decision to the container. The extra event exists so
 * tags can trigger on consent arriving, rather than on page load — a tag
 * that fired on load while everything was still `denied` never runs again
 * by itself.
 */
export function applyConsent(choices) {
  const signals = consentSignals(choices);
  gtag("consent", "update", signals);
  dataLayer().push({ event: "tl_consent_update", ...signals });
}

export function pushEvent(name, params = {}) {
  dataLayer().push({ event: name, ...params });
}

export function pushPageView(pathname) {
  dataLayer().push({
    event: "page_view",
    page_path: pathname,
    page_location: window.location.href,
    page_title: document.title,
  });
}

/**
 * Google's own cookies, removed on withdrawal. A `consent update` to
 * `denied` stops further tag firing but leaves everything already stored
 * on the device, and GA writes `_ga` on the broadest domain it can — so
 * each name has to be cleared against the exact host and against the
 * registrable domain, since a delete only lands when domain and path match
 * how the cookie was set.
 */
export function deleteGoogleCookies() {
  const host = window.location.hostname;
  const apex = host.split(".").slice(-2).join(".");
  const domains = [undefined, host, `.${host}`, apex, `.${apex}`];
  for (const cookie of document.cookie.split(";")) {
    const name = cookie.split("=")[0].trim();
    if (!/^(_ga|_gid$|_gcl_)/.test(name)) continue;
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; Path=/${domain ? `; Domain=${domain}` : ""}`;
    }
  }
}
